import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/site/Reveal";

// Les contacts du test sont enregistrés par l'API Boussole (qui envoie aussi les emails).
const LEADS_URL = "https://boussole-api-s570.onrender.com/api/leads";

// Chaque question : thème (pour repérer point fort / frein), texte [moi, enfant], 4 réponses de 1 à 4 points.
// Une réponse peut être un texte unique ou un couple [moi, enfant].
const q = (id, theme, text, opts) => ({ id, theme, text, opts });

const COMMON = [
  q("c1", "cap", ["Avez-vous un objectif clair pour les prochains mois ?", "Votre enfant a-t-il un objectif clair pour les prochains mois ?"],
    ["Non, aucun pour l'instant", "Une idée encore vague", "Oui, mais sans plan précis", "Oui, avec un plan"]),
  q("c2", "regularite", ["Vous arrive-t-il de remettre à plus tard ce qui compte ?", "Votre enfant remet-il à plus tard ce qui compte ?"],
    ["Souvent", "Régulièrement", "Rarement", "Presque jamais"]),
  q("c3", "confiance", ["Après un échec ou une déception, comment réagissez-vous ?", "Après un échec ou une déception, comment réagit votre enfant ?"],
    [["Je m'en veux longtemps", "Il/elle s'en veut longtemps"], ["Je doute, puis je repars", "Il/elle doute, puis repart"], ["J'analyse et j'ajuste", "Il/elle analyse et ajuste"], ["Je rebondis vite", "Il/elle rebondit vite"]]),
  q("c4", "soutien", ["Vous sentez-vous soutenu(e) par votre entourage dans vos projets ?", "Votre enfant se sent-il soutenu dans ses projets ?"],
    ["Pas vraiment", "Un peu", "Plutôt oui", "Oui, clairement"]),
];

const BY_PROFILE = {
  etudiant: [
    q("e1", "stress", ["Comment vivez-vous les évaluations et les examens ?", "Comment votre enfant vit-il les évaluations et les examens ?"],
      [["Avec beaucoup de stress", "Avec beaucoup de stress"], "Avec un stress qui gêne parfois", "Avec un stress plutôt maîtrisé", "Avec sérénité"]),
    q("e2", "regularite", ["Comment organisez-vous vos révisions ?", "Comment votre enfant organise-t-il ses révisions ?"],
      ["Au dernier moment", "De façon irrégulière", "Avec un planning, parfois suivi", "Avec une méthode régulière"]),
    q("e3", "soutien", ["Quand une notion n'est pas comprise, demandez-vous de l'aide ?", "Quand une notion n'est pas comprise, votre enfant demande-t-il de l'aide ?"],
      ["Jamais", "Rarement", "Souvent", "Toujours, rapidement"]),
    q("e4", "confiance", ["Quel rapport avez-vous avec les notes ?", "Quel rapport votre enfant a-t-il avec les notes ?"],
      ["Elles donnent le sentiment d'être « nul(le) »", "Elles pèsent beaucoup sur le moral", "Elles informent, sans définir", "Elles servent à s'améliorer"]),
    q("e5", "cap", ["Savez-vous quelle suite donner à vos études (orientation, stage, alternance) ?", "Votre enfant sait-il quelle suite donner à ses études (orientation, stage, alternance) ?"],
      ["Aucune idée pour l'instant", "Quelques pistes", "Une idée assez claire", "Un projet précis, avec des étapes"]),
    q("e6", "concentration", ["Quand vous travaillez, restez-vous concentré(e) sans vous disperser ?", "Quand il/elle travaille, votre enfant reste-t-il concentré sans se disperser ?"],
      ["Rarement", "Par moments", "Le plus souvent", "Presque toujours"]),
  ],
  performer: [
    q("p1", "stress", ["Avant une échéance importante (compétition, test, match), comment vous sentez-vous ?", "Avant une échéance importante (compétition, test, match), comment se sent votre enfant ?"],
      ["Submergé(e) par le trac", "Tendu(e), avec du mal à se canaliser", "Concentré(e), avec un peu de trac", "Prêt(e) et en confiance"]),
    q("p2", "concentration", ["Pendant l'effort, arrivez-vous à rester concentré(e) ?", "Pendant l'effort, votre enfant reste-t-il concentré ?"],
      ["Rarement", "Par moments", "Le plus souvent", "Presque toujours"]),
    q("p3", "confiance", ["Après une erreur en pleine action, que se passe-t-il ?", "Après une erreur en pleine action, que se passe-t-il pour votre enfant ?"],
      ["Elle prend toute la place", "Elle gêne pour la suite", "Elle est vite digérée", "Elle sert d'information pour ajuster"]),
    q("p4", "regularite", ["Comment est la régularité à l'entraînement ?", "Comment est la régularité de votre enfant à l'entraînement ?"],
      ["Très variable", "Moyenne", "Plutôt bonne", "Solide"]),
    q("p5", "cap", ["Avez-vous un objectif clair pour votre saison ou votre carrière sportive ?", "Votre enfant a-t-il un objectif clair pour sa saison ou sa carrière sportive ?"],
      ["Aucun pour l'instant", "Une idée encore vague", "Oui, mais sans plan précis", "Oui, avec un plan"]),
    q("p6", "regularite", ["Avez-vous une routine de préparation avant l'effort ?", "Votre enfant a-t-il une routine de préparation avant l'effort ?"],
      ["Aucune", "Elle varie beaucoup", "Oui, assez régulière", "Oui, solide et personnelle"]),
    q("p7", "confiance", ["Pendant l'effort, des pensées parasites (« je vais rater ») vous gênent-elles ?", "Pendant l'effort, des pensées parasites (« je vais rater ») gênent-elles votre enfant ?"],
      ["Très souvent", "Souvent", "Parfois", "Rarement"]),
  ],
  adulte: [
    q("a1", "soutien", ["Comment décririez-vous l'équilibre entre votre vie professionnelle et personnelle ?", "Comment décririez-vous l'équilibre de votre enfant entre ses activités et son temps de repos ?"],
      ["Très déséquilibré", "Plutôt déséquilibré", "Assez équilibré", "Équilibré"]),
    q("a2", "cap", ["Votre projet (travail, reconversion, projet personnel) est-il clair ?", "Son projet d'avenir (études, travail, projet personnel) est-il clair ?"],
      ["Pas du tout", "Quelques pistes", "Une direction, sans étapes", "Une direction et des étapes"]),
    q("a3", "confiance", ["Quand il faut se lancer, que ressentez-vous ?", "Quand il faut se lancer, que ressent votre enfant ?"],
      ["Une peur qui bloque", "Une hésitation forte", "Un peu d'appréhension", "De l'envie"]),
    q("a4", "energie", ["Comment est votre énergie au quotidien ?", "Comment est l'énergie de votre enfant au quotidien ?"],
      ["Souvent épuisée", "Fluctuante", "Plutôt bonne", "Très bonne"]),
    q("a5", "cap", ["Savez-vous quel métier ou quelle formation vous plairait vraiment ?", "Savez-vous quel métier ou quelle formation lui plairait vraiment ?"],
      ["Aucune idée pour l'instant", "Quelques pistes", "Une idée assez claire", "Un projet précis"]),
    q("a6", "alignement", ["Vos choix actuels sont-ils en accord avec vos valeurs et ce qui compte pour vous ?", "Ses choix actuels sont-ils en accord avec ses valeurs ?"],
      ["Pas du tout", "Un peu", "Plutôt oui", "Tout à fait"]),
  ],
};

const THEMES = {
  cap: { strength: "Un cap déjà dessiné", brake: "Un objectif encore à préciser, avec des étapes concrètes" },
  regularite: { strength: "Une bonne régularité", brake: "La régularité : passer de l'intention à des habitudes tenables" },
  confiance: { strength: "Une bonne capacité à rebondir", brake: "La confiance et la façon de vivre l'échec" },
  soutien: { strength: "Un entourage et un équilibre qui aident", brake: "Le soutien et l'équilibre au quotidien" },
  stress: { strength: "Un stress plutôt bien géré", brake: "La gestion de la pression et du stress" },
  concentration: { strength: "Une bonne concentration", brake: "La concentration pendant l'effort" },
  energie: { strength: "Une bonne énergie", brake: "L'énergie et la récupération" },
  alignement: { strength: "Des choix en accord avec ses valeurs", brake: "L'accord entre ses choix et ce qui compte vraiment pour soi" },
};

const PROFILES = [
  { id: "etudiant", title: "Collégien, lycéen ou étudiant", desc: "Méthode de travail, contrôles, orientation, stage ou alternance.", space: "Espace Étudiant" },
  { id: "performer", title: "Sportif ou compétiteur", desc: "Routine de performance, gestion de la pression, cap sportif.", space: "Espace Performer" },
  { id: "adulte", title: "Adulte en reconversion ou en quête de cap", desc: "Trouver une orientation, un métier ou une formation qui vous ressemble.", space: "Espace Jeune adulte · cap de vie" },
];

const CHILD_AGES = ["11-14 ans", "15-17 ans", "18 ans et plus"];
const WISHES_BY = {
  etudiant: ["Reprendre confiance en soi", "Mieux gérer le stress des contrôles", "Retrouver de la motivation", "Trouver une orientation"],
  performer: ["Gagner en confiance en compétition", "Construire une routine de performance", "Mieux gérer la pression", "Progresser dans son sport"],
  adulte: ["Trouver une orientation", "Retrouver de la motivation", "Reprendre confiance en soi"],
};

// On ne recommande jamais la formule Autonomie : toujours l'accompagnement (Cercle Collectif ou Suivi Individuel).
const LEVELS = [
  { name: "Poser le cap", formule: "Cercle Collectif", price: "29,90 € / 4 semaines", why: "L'application complète, plus un rendez-vous collectif tous les 15 jours pour poser un cap clair, garder l'élan et ajuster ensemble.", anchor: "collectif" },
  { name: "Structurer l'élan", formule: "Cercle Collectif", price: "29,90 € / 4 semaines", why: "L'application complète, plus un rendez-vous collectif tous les 15 jours pour structurer le travail, tenir dans la durée et rebondir avec une communauté bienveillante.", anchor: "collectif" },
  { name: "Affûter la performance", formule: "Suivi Individuel", price: "89 € / 4 semaines", why: "Un coaching individuel tous les 15 jours et un travail personnalisé pour passer un cap.", anchor: "individuel" },
];

const T = (v, who) => (Array.isArray(v) ? v[who === "enfant" ? 1 : 0] : v);

export default function Test() {
  const preset = useMemo(() => {
    try { const p = new URLSearchParams(window.location.search).get("profil"); return PROFILES.some((x) => x.id === p) ? p : null; } catch (e) { return null; }
  }, []);
  const [who, setWho] = useState(preset === "adulte" ? "moi" : null);
  const [age, setAge] = useState(null);
  const [wish, setWish] = useState(null);
  const [profile, setProfile] = useState(preset);
  const [ans, setAns] = useState({}); // id -> points (1-4)
  const [i, setI] = useState(0); // index dans l'ordre des écrans
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [trap, setTrap] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  // Écrans : profil -> (qui répond) -> (âge, souhait si jeune) -> questions -> résultat
  const screens = useMemo(() => {
    const s = [];
    if (!preset) s.push("profile");
    if (profile && profile !== "adulte") s.push("who");
    if (who === "enfant") s.push("age", "wish");
    if (profile) [...COMMON, ...BY_PROFILE[profile]].forEach((x) => s.push(x.id));
    s.push("result");
    return s;
  }, [who, profile, preset]);

  const questions = profile ? [...COMMON, ...BY_PROFILE[profile]] : [];
  const cur = screens[Math.min(i, screens.length - 1)];
  const progress = Math.round((Math.min(i, screens.length - 1) / (screens.length - 1)) * 100);

  const next = () => setI((n) => n + 1);
  const back = () => setI((n) => Math.max(0, n - 1));
  const pick = (setter, v) => { setter(v); setTimeout(next, 150); };

  const result = useMemo(() => {
    if (!profile || questions.some((x) => ans[x.id] === undefined)) return null;
    const score = questions.reduce((a, x) => a + ans[x.id], 0);
    const mean = score / questions.length;
    const level = mean <= 2 ? 0 : mean <= 3 ? 1 : 2;
    const byTheme = {};
    questions.forEach((x) => { (byTheme[x.theme] = byTheme[x.theme] || []).push(ans[x.id]); });
    const avg = Object.entries(byTheme).map(([k, v]) => [k, v.reduce((a, b) => a + b, 0) / v.length]);
    avg.sort((a, b) => b[1] - a[1]);
    const strength = avg[0][1] >= 2.5 ? THEMES[avg[0][0]].strength : "Une vraie envie de faire le point : c'est le premier pas";
    const brake = THEMES[avg[avg.length - 1][0]].brake;
    // Pour un jeune, le suivi individuel (avec séance parents) est conseillé dès le niveau intermédiaire.
    const lvl = who === "enfant" && level === 1 ? 2 : level;
    return { score, level: lvl, strength, brake };
  }, [ans, profile, who]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!result || !consent) return;
    setStatus("sending");
    try {
      const L = LEVELS[result.level];
      const answers = questions.map((x) => `${T(x.text, who)} → ${T(x.opts[ans[x.id] - 1], who)}`);
      const r = await fetch(LEADS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email, consent, for_whom: who, child_age: age, profile, score: result.score,
          level: L.name, formule: `${L.formule} (${L.price})`, strength: result.strength, brake: result.brake,
          wish: wish || "", answers, website: trap,
        }),
      });
      if (!r.ok) throw new Error("bad");
      setStatus("done");
      if (window.gtag) window.gtag("event", "generate_lead", { profile });
    } catch (err) {
      setStatus("error");
    }
  };

  const Card = ({ children }) => (
    <div className="rounded-3xl border border-white/10 bg-coal p-6 sm:p-9">{children}</div>
  );
  const Option = ({ label, sub, onClick, selected }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl border px-5 py-4 transition-colors duration-200 ${selected ? "border-gold bg-white/10" : "border-white/10 hover:border-gold/60 hover:bg-white/5"}`}
    >
      <span className="block text-sm sm:text-base font-semibold">{label}</span>
      {sub && <span className="block mt-1 text-xs sm:text-sm text-slate-400">{sub}</span>}
    </button>
  );

  const qi = questions.find((x) => x.id === cur);

  return (
    <div className="min-h-screen bg-obsidian text-chalk font-sans px-5 py-10" data-testid="test-page">
      <div className="max-w-xl mx-auto">
        <a href="/" className="inline-flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity">
          <Logo size={34} />
          <span className="font-heading font-extrabold uppercase tracking-wide text-sm">Yassine Abou</span>
        </a>

        {cur !== "result" && (
          <div className="mb-6" aria-hidden="true">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gold transition-all duration-300" style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        {i === 0 && (
          <div className="mb-5">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase">Quel est votre prochain cap ?</h1>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">Un test gratuit de 2 minutes, adapté à votre profil, pour faire le point et recevoir une recommandation sur mesure. Aucune donnée n'est demandée avant le résultat.</p>
          </div>
        )}

        {cur === "profile" && (
          <Card>
            <h2 className="font-heading text-xl font-extrabold uppercase">Quel test voulez-vous faire ?</h2>
            <p className="mt-2 text-sm text-slate-400">Choisissez le profil qui correspond le mieux (pour vous ou pour votre enfant).</p>
            <div className="mt-5 space-y-3">
              {PROFILES.map((p) => (
                <Option key={p.id} label={p.title} sub={p.desc} selected={profile === p.id}
                  onClick={() => { if (profile !== p.id) setAns({}); setProfile(p.id); setAge(null); setWish(null); setWho(p.id === "adulte" ? "moi" : null); setTimeout(next, 150); }} />
              ))}
            </div>
          </Card>
        )}

        {cur === "who" && (
          <Card>
            <h2 className="font-heading text-xl font-extrabold uppercase">Qui répond à ce test ?</h2>
            <div className="mt-5 space-y-3">
              <Option label="Pour moi" selected={who === "moi"} onClick={() => { setWho("moi"); setAge(null); setWish(null); setTimeout(next, 150); }} />
              <Option label="Pour mon enfant ou un jeune que j'accompagne" selected={who === "enfant"} onClick={() => { setWho("enfant"); setTimeout(next, 150); }} />
            </div>
          </Card>
        )}

        {cur === "age" && (
          <Card>
            <h2 className="font-heading text-xl font-extrabold uppercase">Quel âge a le jeune ?</h2>
            <div className="mt-5 space-y-3">{CHILD_AGES.map((a) => <Option key={a} label={a} selected={age === a} onClick={() => pick(setAge, a)} />)}</div>
          </Card>
        )}

        {cur === "wish" && (
          <Card>
            <h2 className="font-heading text-xl font-extrabold uppercase">Votre souhait principal pour lui ou elle ?</h2>
            <div className="mt-5 space-y-3">{(WISHES_BY[profile] || WISHES_BY.etudiant).map((w) => <Option key={w} label={w} selected={wish === w} onClick={() => pick(setWish, w)} />)}</div>
          </Card>
        )}

        {qi && (
          <Card>
            <p className="text-xs uppercase tracking-wide text-slate-500">Question {questions.indexOf(qi) + 1} sur {questions.length}</p>
            <h2 className="mt-2 font-heading text-lg sm:text-xl font-bold">{T(qi.text, who)}</h2>
            <div className="mt-5 space-y-3">
              {qi.opts.map((o, k) => (
                <Option key={k} label={T(o, who)} selected={ans[qi.id] === k + 1}
                  onClick={() => { setAns((a) => ({ ...a, [qi.id]: k + 1 })); setTimeout(next, 150); }} />
              ))}
            </div>
          </Card>
        )}

        {cur === "result" && result && (() => {
          const L = LEVELS[result.level];
          const P = PROFILES.find((p) => p.id === profile);
          return (
            <div className="space-y-6" data-testid="test-result">
              <Card>
                <p className="text-xs uppercase tracking-wide text-gold font-bold">Votre résultat</p>
                <h1 className="mt-2 font-heading text-3xl font-extrabold uppercase">{L.name}</h1>
                <div className="mt-5 space-y-3 text-sm sm:text-base text-slate-300 leading-relaxed">
                  <p><b className="text-chalk">Point fort :</b> {result.strength}.</p>
                  <p><b className="text-chalk">Piste de progrès :</b> {result.brake}.</p>
                  <p><b className="text-chalk">Espace conseillé :</b> {P.space}.</p>
                  <p><b className="text-chalk">Formule conseillée :</b> {L.formule} ({L.price}). {L.why}</p>
                  <p><b className="text-chalk">Premier pas :</b> choisir une seule priorité pour cette semaine, puis une petite action précise à réaliser. C'est le principe de l'amélioration par petits pas, que l'application Boussole vous aide à suivre.</p>
                </div>
                <a href={`/#accompagnement`} className="mt-6 inline-flex rounded-full bg-ember hover:bg-emberdark px-7 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200">Voir les formules</a>
              </Card>

              <Card>
                {status === "done" ? (
                  <div className="text-center" data-testid="test-lead-done">
                    <CheckCircle2 size={44} className="mx-auto text-gold" />
                    <h2 className="mt-4 font-heading text-xl font-extrabold uppercase">C'est envoyé !</h2>
                    <p className="mt-3 text-sm text-slate-300">Vérifiez votre boîte mail (et vos spams) : votre bilan et votre premier exercice arrivent. Yassine peut aussi vous répondre personnellement.</p>
                  </div>
                ) : (
                  <form onSubmit={submit}>
                    <h2 className="font-heading text-xl font-extrabold uppercase">Recevoir mon bilan détaillé et un premier exercice offert</h2>
                    <label htmlFor="lead-email" className="mt-5 block text-sm font-semibold">Votre adresse email</label>
                    <input id="lead-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                      className="mt-2 w-full rounded-xl border border-white/15 bg-obsidian px-4 py-3 text-sm focus:outline-none focus:border-gold" placeholder="vous@exemple.fr" />
                    <input type="text" tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)} className="hidden" aria-hidden="true" name="website" />
                    <label className="mt-4 flex items-start gap-3 text-xs sm:text-sm text-slate-300 leading-relaxed">
                      <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" required />
                      <span>J'accepte que Yassine Abou utilise mon adresse email et mes réponses pour m'envoyer mon bilan et me recontacter au sujet de l'accompagnement. Je peux demander leur suppression à tout moment par simple réponse à l'email. {who === "enfant" ? "Je suis le parent ou le responsable du jeune concerné." : "J'ai 15 ans ou plus, ou l'accord de mon parent."}</span>
                    </label>
                    {status === "error" && <p className="mt-3 text-sm text-ember" role="alert">L'envoi a échoué. Réessayez dans un instant ou écrivez à yassineabou.contact@gmail.com.</p>}
                    <button type="submit" disabled={!consent || status === "sending"}
                      className="mt-5 inline-flex items-center justify-center rounded-full bg-gold text-obsidian px-7 py-3.5 text-xs font-bold uppercase tracking-wide disabled:opacity-50">
                      {status === "sending" ? "Envoi…" : "Recevoir mon bilan"}
                    </button>
                  </form>
                )}
              </Card>
              <p className="text-xs text-slate-500 leading-relaxed">Ce test est un outil d'accompagnement et de préparation mentale. Il ne remplace pas un avis médical ou psychologique.</p>
            </div>
          );
        })()}

        {i > 0 && (
          <button type="button" onClick={back} className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-chalk">
            <ArrowLeft size={16} /> Retour
          </button>
        )}
      </div>
    </div>
  );
}
